/*
 * King Linked Options — reliable two-tap preview on touch devices.
 *
 * WHY: In redirect mode the app renders each option as a plain <a href> link with
 * an image-preview popover shown on mouseenter. On phones it relies on the browser's
 * native hover-emulation to "preview on 1st tap, navigate on 2nd tap". That heuristic
 * only defers the FIRST hover acquisition — once one swatch already holds simulated
 * hover, tapping a DIFFERENT swatch navigates instantly and skips its preview.
 *
 * FIX: Take explicit control on touch devices. Every swatch behaves the same:
 *   1st tap  -> show that swatch's preview (arm it), block navigation
 *   2nd tap  -> navigate (only when the SAME swatch is already armed)
 *   tap another swatch -> switch the preview to it (re-arm), still no navigation
 *   tap elsewhere / scroll -> disarm and hide the preview
 *
 * The preview is driven by dispatching the same mouseenter/mouseleave events the app
 * already listens for, so it stays visually identical to hover on desktop.
 * Desktop / mouse devices are untouched (gated on `(hover: none)`).
 *
 * APP UPDATE 385 -> 395: the app added a `canHover()` guard that skips binding the
 * mouseenter -> popover listeners *and* early-returns inside its own showPopover()
 * whenever it considers the device mobile (viewport <= 768px, or touch + mobile UA).
 * The mouseenter dispatch therefore lands on nothing on a phone: taps still gated
 * correctly, but no preview. Its popover CSS is still shipped and styled at mobile
 * widths, so we render the popover ourselves — same element, classes, markup and
 * positioning as the app's showPopover(), so it looks identical to desktop hover.
 * The dispatch is kept first for touch devices where the app still binds it.
 */
(function () {
  'use strict';

  // Only touch/hover-less devices exhibit the native hover-emulation quirk.
  if (!window.matchMedia || !window.matchMedia('(hover: none)').matches) return;

  var LINK  = '.lv-option-item-link';
  var SCOPE = 'king-linked-options-product[data-render-context="product-page"]';
  var TAP_MAX_MS = 500;   // longer press is not a tap
  var MOVE_MAX_PX = 10;   // finger drift beyond this is a scroll, not a tap

  var armed = null;       // currently armed link (<a>)
  var armedWrap = null;   // its .lv-option-wrapper (preview currently shown)
  var startX = 0, startY = 0, startT = 0, moved = false;

  // The app centres the popover over the swatch (inline `left` + translateX(-50%))
  // with no viewport clamping, so a preview for an edge swatch overflows off-screen.
  // Nudge it back inside the viewport. The popover is a plain image box (no caret),
  // so a horizontal shift is safe.
  function clampPopoverIntoView() {
    var margin = 8;
    var vw = document.documentElement.clientWidth;
    var els = document.querySelectorAll('.lv-option-popover.show, .lv-option-tooltip.show');
    for (var i = 0; i < els.length; i++) {
      var pop = els[i];
      var r = pop.getBoundingClientRect();
      var cur = parseFloat(pop.style.left) || 0;
      var delta = 0;
      if (r.left < margin) delta = margin - r.left;
      else if (r.right > vw - margin) delta = (vw - margin) - r.right;
      if (delta) pop.style.left = (cur + delta) + 'px';
    }
  }

  // Mirrors the app's own showPopover(): same shared element, class names, child
  // markup and anchor maths, so it inherits the app's stylesheet verbatim.
  function renderPopover(wrap) {
    var image = wrap.getAttribute('data-image');
    if (!image) return;

    var pop = document.querySelector('.lv-option-popover');
    if (!pop) {
      pop = document.createElement('div');
      pop.className = 'lv-option-popover';
      document.body.appendChild(pop);
    }

    var name = wrap.getAttribute('data-name') || '';
    var box = document.createElement('div');
    var img = document.createElement('img');
    img.src = image;
    img.className = 'lv-option-popover__image';
    img.alt = name;
    box.appendChild(img);
    var label = document.createElement('div');
    label.className = 'lv-option-popover__name';
    label.innerText = name;
    box.appendChild(label);

    pop.innerHTML = '';
    pop.appendChild(box);
    var r = wrap.getBoundingClientRect();
    pop.style.top = r.top + window.scrollY - 6 + 'px';
    pop.style.left = r.left + r.width / 2 + 'px';
    pop.classList.add('show');
  }

  function showPreview(wrap) {
    if (!wrap) return;
    // Preferred path: let the app draw its own popover, where it still binds one.
    wrap.dispatchEvent(new MouseEvent('mouseenter'));
    // Since app build 395 that is a no-op on phones — draw it ourselves instead.
    if (!document.querySelector('.lv-option-popover.show')) renderPopover(wrap);
    clampPopoverIntoView();
  }
  function hidePreview() {
    if (armedWrap) armedWrap.dispatchEvent(new MouseEvent('mouseleave'));
    var shown = document.querySelectorAll('.lv-option-popover.show, .lv-option-tooltip.show');
    for (var i = 0; i < shown.length; i++) shown[i].classList.remove('show');
  }
  function disarm() {
    hidePreview();
    armed = null;
    armedWrap = null;
  }

  document.addEventListener('touchstart', function (e) {
    var t = e.changedTouches && e.changedTouches[0];
    startX = t ? t.clientX : 0;
    startY = t ? t.clientY : 0;
    startT = Date.now();
    moved = false;
  }, { passive: true });

  document.addEventListener('touchmove', function (e) {
    var t = e.changedTouches && e.changedTouches[0];
    if (!t) { moved = true; return; }
    if (Math.abs(t.clientX - startX) > MOVE_MAX_PX ||
        Math.abs(t.clientY - startY) > MOVE_MAX_PX) {
      moved = true;
    }
  }, { passive: true });

  document.addEventListener('touchend', function (e) {
    var link = e.target.closest ? e.target.closest(LINK) : null;

    // Tap landed outside a linked-options swatch -> reset any pending preview.
    if (!link || !link.closest(SCOPE)) {
      if (armed) disarm();
      return;
    }
    // Not a clean tap (scroll or long-press) -> let it be.
    if (moved || (Date.now() - startT) > TAP_MAX_MS) return;

    // We own this gesture: block the native click, navigation and hover-synthesis.
    e.preventDefault();
    e.stopPropagation();

    if (link === armed) {
      // Second tap on the already-previewed swatch -> go.
      var url = link.getAttribute('href');
      if (url && url !== '#') window.location.href = url;
      return;
    }

    // First tap on this swatch (or switching from another) -> preview it.
    hidePreview();
    armed = link;
    armedWrap = link.closest('.lv-option-wrapper');
    showPreview(armedWrap);
  }, { passive: false, capture: true });
})();
